import { Flex, LoadingOverlay, Skeleton, Title } from "@mantine/core";
import { useParams, usePathname } from "next/navigation";
import { type PropsWithChildren, type ReactNode, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { TableHeaderRightContent } from "./table-header-right-content";
import { Header } from "@/components/header";
import { ImpersonationBanner } from "@/components/impersonationBanner";
import {
  DefaultHeaderMenu,
  type HeaderMenuGenerationStatus,
  SCREENER_BUILDER_NAV_WIDTH_PX,
  ScreenerBuilderNavigation,
} from "@/components/screener-builder/common";
import { DefaultBottomBarButtons } from "@/components/screener-builder/common/default-bottom-bar-buttons";
import {
  BREADCRUMB_LOADING,
  TableHeader,
} from "@/components/table/tableHeader";
import { useImpersonationBannerHeight } from "@/hooks/use-impersonation-banner-height";
import {
  GenerationStatus,
  ScreenerBuilderStep,
} from "@/types/screener-builder";
import { ScreenerStatus } from "@/types/screeners";
import { screenerIdIsNew } from "@/utils/screener-builder";
import { capitalizeFirstLetter, LAYOUT_DIMENSIONS } from "@/utils/ui";

export interface ScreenerBuilderWrapperProps {
  bottomBarButtons: ReactNode;
  furthestProgressedStep: ScreenerBuilderStep;
  generationStatus: HeaderMenuGenerationStatus;
  hideBottomBar?: boolean;
  isLegacyScreener?: boolean;
  isLoading?: boolean;
  jobDisplayName?: string;
  onPublishClick?: () => void;
  pageTitle: ReactNode;
  preventScroll?: boolean;
  rightSection?: ReactNode;
  screenerStatus?: ScreenerStatus;
  tableHeaderMenu?: ReactNode;
}

const SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX = 38;
const SCREENER_BUILDER_TITLE_SECTION_MARGIN_BOTTOM_PX = 18;
export const TOTAL_SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX =
  SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX +
  SCREENER_BUILDER_TITLE_SECTION_MARGIN_BOTTOM_PX;
export const SCREENER_BUILDER_BOTTOM_BAR_HEIGHT_PX = 68;
export const SCREENER_BUILDER_WRAPPER_LOADING_PROPS: ScreenerBuilderWrapperProps =
  {
    bottomBarButtons: (
      <DefaultBottomBarButtons onNext={() => {}} onSave={() => {}} />
    ),
    furthestProgressedStep: ScreenerBuilderStep.Position,
    generationStatus: GenerationStatus.New,
    isLoading: true,
    pageTitle: <Skeleton h={30} w={150} />,
  };

export const ScreenerBuilderWrapper = ({
  bottomBarButtons,
  children,
  furthestProgressedStep,
  generationStatus,
  hideBottomBar = false,
  isLegacyScreener = false,
  isLoading = false,
  jobDisplayName,
  onPublishClick,
  pageTitle,
  preventScroll = false,
  rightSection,
  tableHeaderMenu = <DefaultHeaderMenu generationStatus={generationStatus} />,
}: PropsWithChildren<ScreenerBuilderWrapperProps>) => {
  const impersonationBannerHeight = useImpersonationBannerHeight();
  const { id: screenerId } = useParams<{ id: string }>();
  const pathname = usePathname() ?? "";

  const breadcrumbPageName = useMemo(() => {
    const step = pathname.split("/")[3];
    if (step === "review") {
      return "Questions";
    }
    return step ? capitalizeFirstLetter(step) : "";
  }, [pathname]);

  return (
    <Flex className={"bg-[#F9F7FD] h-dvh w-dvw"} direction="column">
      <Flex direction="column">
        <Header />
        <TableHeader
          breadCrumbs={[
            { href: "/", text: "All Interviewers" },
            jobDisplayName
              ? {
                  href: screenerIdIsNew(screenerId)
                    ? undefined
                    : `/screener/${screenerId}`,
                  text: jobDisplayName,
                }
              : BREADCRUMB_LOADING,
            ...(breadcrumbPageName ? [{ text: breadcrumbPageName }] : []),
          ]}
          rightContent={
            <TableHeaderRightContent
              menu={tableHeaderMenu}
              onPublishClick={onPublishClick}
            />
          }
        />
      </Flex>
      <Flex className="relative" direction="row">
        <ScreenerBuilderNavigation
          furthestProgressedStep={furthestProgressedStep}
          isLegacyScreener={isLegacyScreener}
        />
        <Flex
          className={twMerge([
            "flex-col",
            "pt-[21px]",
            "px-[18px]",
            "w-full",
            preventScroll ? "overflow-y-hidden" : "overflow-y-scroll",
          ])}
          style={{
            maxHeight: `calc(100dvh - ${
              (hideBottomBar ? 0 : SCREENER_BUILDER_BOTTOM_BAR_HEIGHT_PX) +
              LAYOUT_DIMENSIONS.combinedHeaderHeight +
              impersonationBannerHeight
            }px)`,
          }}
        >
          <Flex
            className="items-center justify-between w-full"
            mb={SCREENER_BUILDER_TITLE_SECTION_MARGIN_BOTTOM_PX}
            h={SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX}
          >
            {typeof pageTitle === "string" ? (
              <Title className="font-semibold" order={3}>
                {pageTitle}
              </Title>
            ) : (
              pageTitle
            )}
            {rightSection}
          </Flex>
          <div
            style={{
              height: `calc(100% - ${TOTAL_SCREENER_BUILDER_TITLE_SECTION_HEIGHT_PX}px)`,
            }}
          >
            <LoadingOverlay visible={isLoading} />
            {children}
          </div>
        </Flex>
      </Flex>
      {!hideBottomBar && (
        <Flex
          className={twMerge([
            "bg-white",
            "border-t",
            "border-uw-gray-2",
            "fixed",
            "items-center",
            "justify-between",
            "px-[18px]",
            "right-0",
          ])}
          style={{
            bottom: `${impersonationBannerHeight}px`,
            height: SCREENER_BUILDER_BOTTOM_BAR_HEIGHT_PX,
            width: `calc(100dvw - ${SCREENER_BUILDER_NAV_WIDTH_PX}px)`,
          }}
        >
          {bottomBarButtons}
        </Flex>
      )}
      <ImpersonationBanner disabled />
    </Flex>
  );
};
